import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from '@/lib/api';
import { usePhoneSystemHealth } from '@/app/providers/phone-system-provider';
import { queryKeys } from '@/lib/query';
import type { Bootstrap } from '@/lib/types';

/**
 * The capability keys that are plain booleans. `capabilities.pages` is a map, so
 * excluding it here keeps `can('pages')` — which would always be true — from
 * type-checking. Page access goes through `canViewPage` / `canEditPage`.
 */
export type BooleanCapability = {
  [K in keyof Bootstrap['capabilities']]: Bootstrap['capabilities'][K] extends boolean ? K : never;
}[keyof Bootstrap['capabilities']];

interface SessionContextValue {
  bootstrap: Bootstrap;
  /** True for the full-admin role; reviewers get a read-mostly console. */
  isFullAdmin: boolean;
  isReviewer: boolean;
  can: (capability: BooleanCapability) => boolean;
  /** Per-page access from the Access control tab. Unknown keys are denied. */
  canViewPage: (key: string) => boolean;
  canEditPage: (key: string) => boolean;
  /**
   * The page key a console path belongs to, or null when the path is not a
   * sidebar page. Detail routes resolve to their list page, so `/calls/42`
   * answers `calls`.
   */
  pageKeyForPath: (path: string) => string | null;
  /** Server clock offset in ms, so durations do not drift with a skewed client. */
  serverSkewMs: number;
}

const SessionContext = React.createContext<SessionContextValue | null>(null);

/**
 * Longest-prefix match of a path against the route -> page key table.
 *
 * Longest first because `/data/backend.pushtoken` and `/data` would both match a
 * path under the former, and the more specific one is the right answer. Matching
 * on a `/` boundary keeps `/callsomething` from resolving to `/calls`.
 */
function matchPageKey(routes: Record<string, string>, path: string): string | null {
  const clean = (path.split('?')[0] ?? path).replace(/\/+$/, '') || '/';
  let bestRoute = '';
  let bestKey: string | null = null;
  for (const [route, key] of Object.entries(routes)) {
    if (clean !== route && !clean.startsWith(`${route}/`)) continue;
    if (route.length > bestRoute.length) {
      bestRoute = route;
      bestKey = key;
    }
  }
  return bestKey;
}

/** Where Django's admin login lives; used when the session is missing. */
export const LOGIN_URL = '/admin/login/';

export function loginRedirect(): void {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`${LOGIN_URL}?next=${next}`);
}

export function SessionProvider({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback: (state: { loading: boolean; error: unknown; retry: () => void }) => React.ReactElement;
}) {
  const phoneSystem = usePhoneSystemHealth();
  const sawPhoneOutage = React.useRef(false);
  const query = useQuery({
    queryKey: queryKeys.bootstrap,
    queryFn: () => api.get<Bootstrap>('/bootstrap/'),
    // The nav, role, and badge counts are stable for a session; refetching on
    // every focus would be a wasted round trip on every tab switch.
    staleTime: 5 * 60_000,
    retry: false,
  });

  React.useEffect(() => {
    if (phoneSystem?.backend_healthy === false) {
      sawPhoneOutage.current = true;
    } else if (phoneSystem?.backend_healthy === true && sawPhoneOutage.current) {
      sawPhoneOutage.current = false;
      if (query.isError) void query.refetch();
    }
  }, [phoneSystem?.backend_healthy, query.isError, query.refetch]);

  // A missing or expired session cannot be recovered in-app: hand off to Django's
  // login so AdminRoleAuthenticationForm still governs who gets in.
  React.useEffect(() => {
    if (query.error instanceof ApiError && query.error.isAuthError) {
      loginRedirect();
    }
  }, [query.error]);

  const value = React.useMemo<SessionContextValue | null>(() => {
    if (!query.data) return null;
    const bootstrap = query.data;
    const serverTime = new Date(bootstrap.server_time).getTime();
    return {
      bootstrap,
      isFullAdmin: bootstrap.role.is_full_admin,
      isReviewer: bootstrap.role.is_reviewer,
      can: (capability) => Boolean(bootstrap.capabilities?.[capability]),
      // Fail closed: a key the server did not send is one this user cannot reach.
      canViewPage: (key) => Boolean(bootstrap.capabilities?.pages?.[key]?.view),
      canEditPage: (key) => Boolean(bootstrap.capabilities?.pages?.[key]?.edit),
      pageKeyForPath: (path) => matchPageKey(bootstrap.page_routes ?? {}, path),
      serverSkewMs: Number.isNaN(serverTime) ? 0 : serverTime - Date.now(),
    };
  }, [query.data]);

  if (!value) {
    return fallback({
      loading: query.isLoading,
      error: query.error,
      retry: () => void query.refetch(),
    });
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = React.useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}

/** Bootstrap payload without the provider assertion, for optional consumers. */
export function useOptionalSession(): SessionContextValue | null {
  return React.useContext(SessionContext);
}
